import { Reveal } from "@/components/Reveal";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const CHAPTER_META = [
    {
        num: "01",
        id: "c01",
        image: "https://images.pexels.com/photos/4922086/pexels-photo-4922086.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    {
        num: "02",
        id: "c02",
        image: "https://images.unsplash.com/photo-1580060839134-75a5edca2e99?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxjYXBlJTIwdG93bnxlbnwwfHx8fDE3ODQ2NzM0MDN8MA&ixlib=rb-4.1.0&q=85",
        flip: true,
    },
    {
        num: "03",
        id: "c03",
        image: "https://images.pexels.com/photos/32609063/pexels-photo-32609063.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    {
        num: "04",
        id: "c04",
        image: "https://images.pexels.com/photos/7079773/pexels-photo-7079773.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        flip: true,
    },
    {
        num: "05",
        id: "c05",
        image: "https://images.pexels.com/photos/10633476/pexels-photo-10633476.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
];

export default function Manifesto() {
    const { t } = useTranslation();
    return (
        <section id="manifesto" className="relative py-32 md:py-40" data-testid="manifesto">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <Reveal>
                    <div className="mb-24 grid md:grid-cols-[1fr_1.4fr] gap-8 md:gap-24 items-end">
                        <div>
                            <div className="overline mb-4">{t("manifesto.overline")}</div>
                            <h2 className="font-serif text-5xl md:text-7xl text-white tracking-tighter leading-[0.95]">
                                {t("manifesto.titleLine1")}<br />
                                <span style={{ fontStyle: "italic", color: "var(--gold-pale)" }}>{t("manifesto.titleLine2")}</span> {t("manifesto.titleLine3")}
                            </h2>
                        </div>
                        <p className="text-white/60 text-lg leading-relaxed max-w-lg justify-self-start md:justify-self-end">
                            {t("manifesto.subtitle")}
                        </p>
                    </div>
                </Reveal>

                <div className="space-y-32 md:space-y-48">
                    {CHAPTER_META.map((c, i) => (
                        <Chapter key={c.num} {...c} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function Chapter({ num, id: chapterKey, image, flip = false, index }) {
    const { t } = useTranslation();
    const kicker = t(`manifesto.chapters.${chapterKey}.kicker`);
    const title = t(`manifesto.chapters.${chapterKey}.title`);
    const body = t(`manifesto.chapters.${chapterKey}.body`);
    const bullets = t(`manifesto.chapters.${chapterKey}.bullets`, { returnObjects: true }) || [];

    return (
        <div className="grid md:grid-cols-12 gap-8 md:gap-16 items-center" data-testid={`chapter-${num}`}>
            <div className={`md:col-span-6 ${flip ? "md:order-2" : ""}`}>
                <Reveal delay={0.05}>
                    <div className="flex items-baseline gap-5 mb-6">
                        <span className="font-mono text-xs text-white/40 tracking-[0.3em]">CH. {num}</span>
                        <span className="h-px flex-1" style={{ background: "rgba(212,175,55,0.3)" }} />
                        <span className="overline">{kicker}</span>
                    </div>
                    <h3 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight mb-6">
                        {title}
                    </h3>
                    <p className="text-white/60 text-lg leading-relaxed max-w-md mb-8">{body}</p>
                    <ul className="space-y-3">
                        {Array.isArray(bullets) && bullets.map((b) => (
                            <li key={b} className="flex items-start gap-3 text-white/80 text-sm">
                                <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "var(--gold)" }} />
                                {b}
                            </li>
                        ))}
                    </ul>
                </Reveal>
            </div>

            <div className={`md:col-span-6 ${flip ? "md:order-1" : ""}`}>
                <Reveal delay={0.15} y={40}>
                    <motion.div
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
                        className="relative rounded-3xl overflow-hidden aspect-[4/5] group"
                    >
                        <img src={image} alt={kicker} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(11,19,32,0.6) 100%)" }} />
                        <div className="absolute bottom-0 left-0 right-0 p-8 flex items-end justify-between">
                            <div>
                                <div className="overline text-white/70">{t("manifesto.chapterLabel", { num })}</div>
                                <div className="font-serif text-xl mt-2 text-white/90">{kicker}</div>
                            </div>
                            <div className="font-serif text-6xl leading-none" style={{ color: "var(--gold)", opacity: 0.85 }}>{num}</div>
                        </div>
                    </motion.div>
                </Reveal>
            </div>
        </div>
    );
}
